/**
 * Fully-paid state shown when the invoice has no outstanding balance.
 */

import { Button } from '@elsapiens/ui'

import { formatAmount } from '../../utils/formatters'

interface RecordPaymentFullyPaidProps {
  invoiceCurrency: string
  totalAmount: number
  paymentsReceived: number
  onClose: () => void
}

interface SummaryCellProps {
  label: string
  amount: number
  currency: string
  isAccent?: boolean
}

function SummaryCell({ label, amount, currency, isAccent }: SummaryCellProps): JSX.Element {
  return (
    <div className="text-center">
      <div className="text-[11px] uppercase tracking-wide text-foreground-secondary font-medium">{label}</div>
      <div className={`font-mono text-base font-semibold mt-0.5 ${isAccent ? 'text-primary' : 'text-foreground'}`}>
        {formatAmount(amount, currency)}
      </div>
    </div>
  )
}

function CheckCircle(): JSX.Element {
  return (
    <div className="relative inline-flex items-center justify-center mx-auto">
      <div className="absolute w-24 h-24 rounded-full bg-accent animate-pulse" />
      <div className="relative w-20 h-20 rounded-full bg-gradient-to-br from-primary to-primary/80 flex items-center justify-center shadow-lg shadow-primary/20">
        <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24">
          <path d="M5 13l4 4L19 7" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </div>
    </div>
  )
}

export function RecordPaymentFullyPaid({
  invoiceCurrency,
  totalAmount,
  paymentsReceived,
  onClose,
}: RecordPaymentFullyPaidProps): JSX.Element {
  return (
    <div className="el-py-xl text-center space-y-5">
      <CheckCircle />
      <div className="space-y-2">
        <h3 className="text-lg font-semibold text-foreground">Fully Paid</h3>
        <p className="text-sm text-foreground-secondary max-w-xs mx-auto">
          This invoice has been settled in full. No further payments can be accepted.
        </p>
      </div>
      <div className="inline-flex items-center gap-6 rounded-xl bg-muted border border-border el-px-lg el-py-sm">
        <SummaryCell amount={totalAmount} currency={invoiceCurrency} label="Invoice Total" />
        <div className="w-px h-8 bg-border" />
        <SummaryCell isAccent amount={paymentsReceived} currency={invoiceCurrency} label="Payments Received" />
        <div className="w-px h-8 bg-border" />
        <SummaryCell isAccent amount={0} currency={invoiceCurrency} label="Balance Due" />
      </div>
      <Button className="mt-2" variant="ghost" onClick={onClose}>Close</Button>
    </div>
  )
}
